import React from 'react';
import Container from 'components/atomic/atoms/container';

/**
 * Dot size scale
 */
export type DotSize = 'sm' | 'md' | 'lg';

const DOT_SIZES: Record<DotSize, string> = {
  sm: '4px',
  md: '6px',
  lg: '8px',
};

/**
 * DotIndicator Props
 */
export interface DotIndicatorProps {
  /** 
   * Dot color - can be:
   * - Hex color: "#1976d2"
   * - MUI theme path: "primary.main"
   * - CSS color name: "blue"
   */
  color: string;
  
  /** Dot size */
  size?: DotSize;
  
  /** Additional CSS class */
  className?: string;
}

/**
 * DotIndicator Component
 * 
 * A simple colored dot for status indicators, bullet points, etc.
 * 
 * Layer 1: Atomic Component - pure presentation, no domain knowledge.
 * 
 * @example
 * ```tsx
 * // Status indicator with hex color
 * <DotIndicator color="#32984E" size="sm" />
 * 
 * // Using MUI theme path (resolved by sx prop)
 * <DotIndicator color="primary.main" size="md" />
 * 
 * // CSS color name
 * <DotIndicator color="green" size="lg" />
 * ```
 */
export function DotIndicator({ 
  color, 
  size = 'md', 
  className,
}: DotIndicatorProps) {
  const dotSize = DOT_SIZES[size];
  
  return (
    <Container
      component="span"
      className={className}
      sx={{
        width: dotSize,
        height: dotSize,
        minWidth: dotSize,
        minHeight: dotSize,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'inline-block',
        flexShrink: 0,
      }}
      aria-hidden="true"
    />
  );
}

export default DotIndicator;
